import { ExpandOutlined, ShrinkOutlined } from '@ant-design/icons';
import type {
  CellRange,
  CellRangeParams,
  CellSelectionChangedEvent,
  GridReadyEvent,
  themeQuartz,
} from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import {
  AgGridReact as AgGridReactComponent,
  AgGridReactProps,
} from 'ag-grid-react';
import { FloatButton, theme } from 'antd';
import defaultEmptyImg from 'assets/logos/empty.svg';
import {
  CSSProperties,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
  ReactNode,
  isValidElement,
  cloneElement,
  useCallback,
} from 'react';

import { FadeIn } from 'components/FadeIn';
import { Tooltip } from 'components/Tooltip';
import { LoadingOverlay } from 'components/table/LoadingOverlay';
import {
  NoRowsOverlay,
  NoRowsOverlayProps,
} from 'components/table/NoRowsOverlay';
import {
  CustomPaginationState,
  TableControls,
} from 'components/table/TableControls';
import {
  getRowClass,
  useTableStyles,
  useTableTheme,
  withCellStyle,
} from 'components/table/tableStyles';

import { aggregationStatusBarConfig } from 'utils/reports/constants';

const NUMERIC_COL_TYPES = new Set(['numericColumn', 'rightAligned']);

const isNumericColType = (type: string | string[] | undefined): boolean => {
  if (!type) return false;
  const types = Array.isArray(type) ? type : [type];
  return types.some((t) => NUMERIC_COL_TYPES.has(t));
};

const toRangeParams = (
  range: Pick<CellRange, 'startRow' | 'endRow'>,
  columns: CellRangeParams['columns']
): CellRangeParams => ({
  rowStartIndex: range.startRow?.rowIndex ?? null,
  rowStartPinned: range.startRow?.rowPinned,
  rowEndIndex: range.endRow?.rowIndex ?? null,
  rowEndPinned: range.endRow?.rowPinned,
  columns,
});

interface PropTypes extends AgGridReactProps {
  id?: string;
  height?: string;
  gridRef?: RefObject<AgGridReact | null>;
  noRowTitle?: string;
  noRowDescription?: string;
  showZoomControls?: boolean;
  emptyImg?: string;
  hideTableControls?: boolean;
  hideFullscreenIcon?: boolean;
  additionalCTA?: ReactNode;
  /** Custom pagination for group-based counting (bypasses AG Grid pagination) */
  customPagination?: CustomPaginationState;
  /** Callback when page size changes (useful with custom pagination) */
  onCustomPageSizeChange?: (pageSize: number) => void;
  /** Enable alternating row colors (zebra striping) */
  alternateRows?: boolean;
  /** Additional theme params to merge with the default theme */
  themeOverrides?: Parameters<typeof themeQuartz.withParams>[0];
  onFullScreenChange?: (isFullScreen: boolean) => void;
  /** Allow cell selection on all columns (by default only numeric columns are selectable when cellSelection is enabled) */
  allowAllColumns?: boolean;
}

export function AgTable(props: PropTypes) {
  const {
    gridRef,
    noRowDescription,
    noRowTitle,
    showZoomControls = true,
    emptyImg,
    hideTableControls = false,
    hideFullscreenIcon = false,
    additionalCTA,
    customPagination,
    onCustomPageSizeChange,
    alternateRows = false,
    className,
    onFullScreenChange,
    themeOverrides,
    cellSelection,
    allowAllColumns = false,
    onCellSelectionChanged,
    ...agGridProps
  } = props;
  const { agGridTheme } = useTableTheme();
  const { styles } = useTableStyles();
  const { token } = theme.useToken();
  const [scale, setScale] = useState(1);
  const [pageSize, setPageSize] = useState(props.paginationPageSize || 30);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const noRowsOverlayComponentParams: NoRowsOverlayProps = useMemo(
    () => ({
      emptyImg: emptyImg ?? defaultEmptyImg,
      noRowTitle: noRowTitle ?? '',
      noRowDescription: noRowDescription ?? '',
    }),
    [noRowDescription, noRowTitle, emptyImg]
  );

  const defaultColDef = useMemo(
    () => ({
      // Only enable autoHeight if not using masterDetail (which conflicts with autoHeight)
      ...(props.masterDetail ? {} : { autoHeight: true, wrapText: true }),
      ...props.defaultColDef,
    }),
    [props.defaultColDef, props.masterDetail]
  );

  const paginationPageSizeSelector = useMemo(
    () => props.paginationPageSizeSelector || [15, 30, 45, 60, 75],
    [props.paginationPageSizeSelector]
  );

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    onCustomPageSizeChange?.(newSize);
    const gridApi = gridRef?.current?.api;
    if (gridApi) {
      gridApi.setGridOption('paginationPageSize', newSize);
    }
  };

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(!isFullScreen);
    setScale(1);
    onFullScreenChange?.(!isFullScreen);
  }, [isFullScreen, onFullScreenChange]);

  // Close fullscreen on Escape key
  useEffect(() => {
    if (!isFullScreen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        toggleFullScreen();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullScreen, toggleFullScreen]);

  const mergedTheme = useMemo(() => {
    if (!themeOverrides) return agGridTheme;
    return agGridTheme.withParams(themeOverrides);
  }, [agGridTheme, themeOverrides]);

  const gridContainerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const isAdjustingRanges = useRef(false);
  const lastValidRanges = useRef<CellRangeParams[]>([]);

  // Deselect cells when clicking outside the table (handles both normal and fullscreen views)
  useEffect(() => {
    if (!cellSelection) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        gridContainerRef.current?.contains(target) ||
        fullscreenContainerRef.current?.contains(target)
      )
        return;
      gridRef?.current?.api?.clearCellSelection();
      lastValidRanges.current = [];
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cellSelection, gridRef]);

  // Restrict cell selection to numeric columns only (unless allowAllColumns)
  const handleCellSelectionChanged = (event: CellSelectionChangedEvent) => {
    onCellSelectionChanged?.(event);
    if (!event.finished || allowAllColumns || isAdjustingRanges.current) return;

    const api = gridRef?.current?.api;
    const ranges = api?.getCellRanges();
    if (!api || !ranges?.length) return;

    const hasNonNumeric = ranges.some((range) =>
      range.columns.some((col) => !isNumericColType(col.getColDef().type))
    );
    if (!hasNonNumeric) {
      // All columns are numeric — snapshot as the last valid selection
      lastValidRanges.current = ranges.map((r) => toRangeParams(r, r.columns));
      return;
    }

    // Filter to only numeric columns across all ranges
    const filteredRanges: CellRangeParams[] = ranges.flatMap((range) => {
      const numericCols = range.columns.filter((col) =>
        isNumericColType(col.getColDef().type)
      );
      return numericCols.length > 0 ? [toRangeParams(range, numericCols)] : [];
    });

    // If no numeric columns in the new selection, restore the previous valid selection
    const rangesToApply =
      filteredRanges.length > 0 ? filteredRanges : lastValidRanges.current;

    isAdjustingRanges.current = true;
    api.clearCellSelection();
    rangesToApply.forEach((range) => api.addCellRange(range));
    lastValidRanges.current = rangesToApply;
    isAdjustingRanges.current = false;
  };

  // Shared AgGrid component configuration
  const agGridConfig = {
    theme: mergedTheme,
    ...(cellSelection && { cellSelection }),
    ...agGridProps,
    gridOptions: {
      headerHeight: 32,
      rowHeight: 32,
      ...agGridProps.gridOptions,
    },
    loadingOverlayComponent: LoadingOverlay,
    animateRows: false,
    ref: gridRef as RefObject<AgGridReact>,
    noRowsOverlayComponent: NoRowsOverlay,
    noRowsOverlayComponentParams,
    // Don't force AG Grid pagination when using custom (group-based) pagination
    pagination: customPagination
      ? false
      : showZoomControls
        ? true
        : props.pagination,
    paginationPageSize: pageSize,
    onPaginationChanged: props.onPaginationChanged,
    onGridReady: (params: GridReadyEvent) => {
      // Preserve any external handler
      if (typeof agGridProps.onGridReady === 'function') {
        agGridProps.onGridReady(params);
      }

      // Initialize pagination display strictly via Grid API
      const apiPageSize = params.api.paginationGetPageSize();
      setPageSize((prev) => (prev !== apiPageSize ? apiPageSize : prev));
    },
    paginationPageSizeSelector,
    suppressPaginationPanel: showZoomControls,
    domLayout: props.domLayout,
    defaultColDef,
    columnMenu: 'legacy' as const,
    suppressMovableColumns: props.suppressMovableColumns ?? true,
    getContextMenuItems:
      props.getContextMenuItems ?? (() => ['export', 'copy']),
    columnDefs: props.columnDefs ? withCellStyle(props.columnDefs) : [],
    autoGroupColumnDef: { ...(props.autoGroupColumnDef || {}) },
    getRowStyle: agGridProps.getRowStyle,
    getRowClass:
      agGridProps.getRowClass ||
      ((params: any) => getRowClass(params, { alternateRows })),
    statusBar:
      props.statusBar ??
      (cellSelection ? aggregationStatusBarConfig : undefined),
    onCellSelectionChanged:
      cellSelection && !allowAllColumns
        ? handleCellSelectionChanged
        : onCellSelectionChanged,
  };

  return (
    <>
      <FadeIn>
        <div
          ref={gridContainerRef}
          className={`h-full w-full flex flex-col relative ${styles.agGrid}`}
        >
          <div className="flex-1 w-full overflow-auto">
            <div
              className={`ag-theme-quartz h-full w-full ${styles.agGrid} ${
                showZoomControls ? 'zoom-table' : ''
              } ${className || ''}`}
              style={
                {
                  '--table-zoom-scale': `scale(${scale})`,
                  width: `${100 / scale}%`,
                  height: `${100 / scale}%`,
                } as CSSProperties
              }
            >
              <AgGridReactComponent {...agGridConfig} />
            </div>
          </div>

          {gridRef && !hideTableControls && (
            <TableControls
              gridRef={gridRef}
              showZoomControls={showZoomControls}
              scale={scale}
              onScaleChange={setScale}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              paginationPageSizeSelector={
                Array.isArray(paginationPageSizeSelector)
                  ? paginationPageSizeSelector
                  : [15, 30, 45, 60, 75]
              }
              pagination={props.pagination}
              customPagination={customPagination}
            />
          )}

          {additionalCTA &&
            (isValidElement<{ style?: CSSProperties }>(additionalCTA)
              ? cloneElement(additionalCTA, {
                  style: {
                    ...(additionalCTA.props.style || {}),
                    position: 'absolute',
                    bottom:
                      showZoomControls || props.pagination ? '54px' : '8px',
                    right: '65px',
                  },
                })
              : null)}

          {!hideFullscreenIcon && (
            <Tooltip title="Open Full Screen">
              <FloatButton
                icon={<ExpandOutlined style={{ fontSize: 16 }} />}
                onClick={toggleFullScreen}
                type="primary"
                className="fullscreen-button opacity-0 transition-opacity duration-200 shadow"
                style={{
                  position: 'absolute',
                  bottom: showZoomControls || props.pagination ? '54px' : '8px',
                  right: '16px',
                }}
              />
            </Tooltip>
          )}
        </div>
      </FadeIn>

      {/* Full Screen Modal */}
      {isFullScreen && (
        <div
          className="fixed inset-0 z-9999 flex flex-col bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={(e) => e.target === e.currentTarget && toggleFullScreen()}
        >
          <div
            ref={fullscreenContainerRef}
            className="bg-white dark:bg-gray-900 flex-1 m-4 rounded-lg shadow-2xl flex flex-col overflow-hidden p-4"
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between mb-2"
              style={{
                backgroundColor: token.colorBgElevated,
                borderColor: token.colorBorder,
              }}
            >
              <h3
                className="text-lg font-semibold"
                style={{ color: token.colorText }}
              >
                Table View
              </h3>

              <Tooltip title="Close Full Screen" placement="left">
                <FloatButton
                  icon={<ShrinkOutlined style={{ fontSize: 16 }} />}
                  onClick={toggleFullScreen}
                  style={{
                    position: 'absolute',
                    right: '44px',
                    bottom: '44px',
                    transform: 'scale(0.9)',
                  }}
                  type="primary"
                />
              </Tooltip>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto">
              <div
                className={`ag-theme-quartz h-full w-full ${styles.agGrid} ${
                  showZoomControls ? 'zoom-table' : ''
                } ${className || ''}`}
                style={
                  {
                    '--table-zoom-scale': `scale(${scale})`,
                    width: `${100 / scale}%`,
                    height: `${100 / scale}%`,
                  } as CSSProperties
                }
              >
                <AgGridReactComponent {...agGridConfig} />
              </div>
            </div>

            {gridRef && !hideTableControls && (
              <TableControls
                gridRef={gridRef}
                showZoomControls={showZoomControls}
                scale={scale}
                onScaleChange={setScale}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                paginationPageSizeSelector={
                  Array.isArray(paginationPageSizeSelector)
                    ? paginationPageSizeSelector
                    : [15, 30, 45, 60, 75]
                }
                pagination={props.pagination}
                customPagination={customPagination}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
